import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { GraficoAndamento } from "@/components/panoramica/GraficoAndamento";
import { StrisciaTotali } from "@/components/panoramica/StrisciaTotali";
import { Suggerimento } from "@/components/panoramica/Suggerimento";
import { TabellaCanali } from "@/components/panoramica/TabellaCanali";
import { VISTE } from "@/components/panoramica/colonne";
import { api } from "@/lib/api";
import { numeriTotale, oraBreve, perPaese, type Metrica, type Paese, type Vista } from "@/lib/panoramica";
import { cn } from "@/lib/utils";

const PAESI: ReadonlyArray<{ id: Paese; etichetta: string }> = [
  { id: "tutti", etichetta: "Tutti" },
  { id: "IT", etichetta: "IT" },
  { id: "ES", etichetta: "ES" },
  { id: "FR", etichetta: "FR" },
];

const ORA_MS = 60 * 60 * 1000;

export function Panoramica(): JSX.Element {
  const dati = useQuery({
    queryKey: ["panoramica"],
    queryFn: api.panoramica,
    // I numeri cambiano una volta l'ora: niente polling, si rilegge tornando sulla scheda.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
  const [paese, setPaese] = useState<Paese>("tutti");
  const [vista, setVista] = useState<Vista>("stato");
  const [metrica, setMetrica] = useState<Metrica>("sotto_margine");
  const [selezionato, setSelezionato] = useState<string | null>(null);
  const adesso = new Date();

  const canali = useMemo(() => perPaese(dati.data?.canali ?? [], paese), [dati.data, paese]);
  const buybox = canali.filter((c) => c.tipo === "buybox");
  const priceSetter = canali.filter((c) => c.tipo === "price_setter");
  const aggiornato = canali
    .map((c) => c.calcolato_at)
    .filter((d): d is string => d !== null)
    .sort()
    .at(-1);

  const scegliPaese = (p: Paese) => {
    setPaese(p);
    const scelto = dati.data?.canali.find((c) => c.marketplace_id === selezionato);
    if (scelto && p !== "tutti" && scelto.country !== p) setSelezionato(null);
  };
  const seleziona = (id: string) => setSelezionato((attuale) => (attuale === id ? null : id));

  return (
    <div className="space-y-6 px-7 pb-10 pt-6">
      <header className="flex flex-wrap items-end gap-3.5">
        <h1 className="page-title">Panoramica</h1>
        {aggiornato ? (
          <span className="pb-0.5 text-label text-ink-3">
            aggiornato alle {oraBreve(aggiornato)} · prossimo ricalcolo{" "}
            {oraBreve(new Date(new Date(aggiornato).getTime() + ORA_MS).toISOString())}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2.5">
          <span className="text-label font-medium text-ink-3">Colonne</span>
          <div className="flex gap-1">
            {VISTE.map((v) => (
              <button
                key={v.id}
                type="button"
                aria-pressed={vista === v.id}
                onClick={() => setVista(v.id)}
                className={cn("btn btn-sm", vista === v.id && "toggle-on")}
              >
                {v.etichetta}
              </button>
            ))}
          </div>
          <span className="mx-0.5 h-5 w-px bg-line-2" aria-hidden="true" />
          <span className="text-label font-medium text-ink-3">Paese</span>
          <div className="flex gap-1">
            {PAESI.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={paese === p.id}
                onClick={() => scegliPaese(p.id)}
                className={cn("btn btn-sm", paese === p.id && "toggle-on")}
              >
                {p.etichetta}
              </button>
            ))}
          </div>
        </div>
      </header>

      {dati.isLoading ? <Segnaposto /> : null}

      {dati.isError && !dati.data ? (
        <div role="alert" className="card-stack flex items-center gap-3 px-5 py-4 text-sm text-ink-2">
          <span>Non riesco a leggere la Panoramica: {dati.error instanceof Error ? dati.error.message : "errore sconosciuto"}.</span>
          <button type="button" className="btn btn-sm ml-auto" onClick={() => void dati.refetch()}>
            Riprova
          </button>
        </div>
      ) : null}

      {dati.data ? (
        <div className={cn("space-y-5 transition-opacity duration-200", dati.isFetching && "opacity-70")}>
          <StrisciaTotali buybox={numeriTotale(buybox)} priceSetter={numeriTotale(priceSetter)} />
          {buybox.length ? (
            <TabellaCanali
              titolo="Canali a buybox"
              tipo="buybox"
              vista={vista}
              canali={buybox}
              selezionato={selezionato}
              onSeleziona={seleziona}
              adesso={adesso}
            />
          ) : null}
          {priceSetter.length ? (
            <TabellaCanali
              titolo="Price-setter"
              sottotitolo={
                <Suggerimento titolo="Price-setter" testo="Canale senza concorrenti visibili, dove AM fissa il prezzo sul margine obiettivo." allinea="sinistra">
                  <span
                    tabIndex={0}
                    className="cursor-help border-b border-dotted border-ink-3 text-label text-ink-3 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                  >
                    canali senza concorrenti visibili
                  </span>
                </Suggerimento>
              }
              tipo="price_setter"
              vista={vista}
              canali={priceSetter}
              selezionato={selezionato}
              onSeleziona={seleziona}
              adesso={adesso}
            />
          ) : null}
          {canali.length === 0 ? <p className="text-sm text-ink-2">Nessun canale abilitato per questo paese.</p> : null}
          <section aria-labelledby="andamento">
            <h2 id="andamento" className="mb-2 text-md font-semibold text-ink">
              Andamento 30 giorni
            </h2>
            <GraficoAndamento
              canali={canali}
              metrica={metrica}
              onMetrica={setMetrica}
              selezionato={selezionato}
              etichettaTotale={paese === "tutti" ? "tutti i canali" : `canali ${paese}`}
            />
          </section>
        </div>
      ) : null}
    </div>
  );
}

function Segnaposto(): JSX.Element {
  // Righe ferme, senza animazione: il primo caricamento dura meno di un secondo.
  return (
    <div className="card-stack space-y-2 p-4" aria-label="Caricamento della Panoramica">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="h-9 rounded-md bg-paper-2" />
      ))}
    </div>
  );
}
